import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Card, Divider, IconButton, Stack, Tab, Tabs, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import PropTypes from 'prop-types';
import SessionChartTab from './SessionChartTab';
import { friendlyNameConverter, getSensorUnit } from '../../../services/localizationService';
import Iconify from '../../../components/iconify';

// A recording's readings come off the wire as strings — `DataPoint.value` is a
// String on the API model — and the chart reads a non-numeric value as an
// absent one, so an unconverted point silently vanishes. `Number('')` is 0,
// which would chart a missing reading as a real zero, so an empty string has to
// be rejected before the conversion rather than after it.
const readingOf = (value) => {
  if (typeof value === 'number') return value;
  if (typeof value !== 'string' || value.trim() === '') return NaN;
  return Number(value);
};

// GPS lives on the map, not on a time chart.
const NON_CHARTABLE = ['altitude', 'latitude', 'longitude'];

SessionPageChartTabbedDisplay.propTypes = {
  session: PropTypes.object,
  events: PropTypes.array,
  currentTime: PropTypes.object,
  onChartClick: PropTypes.func,
  eventSensor: PropTypes.string,
  setSensor: PropTypes.func,
  openExport: PropTypes.func,
  onRefresh: PropTypes.func,
  refreshing: PropTypes.bool,
};

// Turns the recording's `{ "<sensor>:<channel>": [{ time, value }] }` map into
// the channel list the chart selects from. Points are `[time, value]` pairs so
// each channel keeps its own sample clock — channels recorded by different
// devices do not share one.
export function channelsFrom(session) {
  return Object.keys(session?.data || {})
    .filter((key) => !NON_CHARTABLE.includes(key.split(':').pop().toLowerCase()))
    .map((key) => {
      const channel = key.split(':').pop();
      return {
        key,
        channel,
        name: friendlyNameConverter(key).trim(),
        unit: getSensorUnit(channel),
        points: (session.data[key] || []).map((p) => [new Date(p.time).getTime(), readingOf(p.value)]),
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

export default function SessionPageChartTabbedDisplay(props) {
  const channels = useMemo(() => channelsFrom(props.session), [props.session]);

  // One entry per tab. The selection lives here rather than in the chart so a
  // tab keeps its channels while the others are unmounted — mounted-but-hidden
  // would leave ApexCharts measuring a zero-width canvas.
  const [charts, setCharts] = useState([{ id: 1, keys: [] }]);
  const [active, setActive] = useState(0);
  const nextId = useRef(2);

  // The recording's data arrives after the first paint, so a chart that has
  // chosen nothing yet opens on the first channel when the channels land.
  useEffect(() => {
    setCharts((prev) =>
      prev.map((c) => {
        const stillPresent = c.keys.filter((k) => channels.some((ch) => ch.key === k));
        if (stillPresent.length > 0) return stillPresent.length === c.keys.length ? c : { ...c, keys: stillPresent };
        return { ...c, keys: channels[0] ? [channels[0].key] : [] };
      })
    );
  }, [channels]);

  const setKeys = (index, keys) =>
    setCharts((prev) => prev.map((c, i) => (i === index ? { ...c, keys } : c)));

  const addChart = () => {
    setCharts((prev) => {
      const next = [...prev, { id: nextId.current, keys: channels[0] ? [channels[0].key] : [] }];
      nextId.current += 1;
      setActive(next.length - 1);
      return next;
    });
  };

  const closeChart = (index) => {
    setCharts((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== index)));
    setActive((prevActive) => {
      if (charts.length === 1) return prevActive;
      return prevActive > index || prevActive === charts.length - 1
        ? Math.max(0, prevActive - 1)
        : prevActive;
    });
  };

  const current = Math.min(active, charts.length - 1);

  return (
    <Card sx={{ overflow: 'visible', zIndex: 10 }}>
      <Stack direction="row" alignItems="center" sx={{ pr: 1, pl: 1 }}>
        <Tabs
          value={current}
          onChange={(_, v) => setActive(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ minHeight: 48, flexGrow: 1 }}
        >
          {charts.map((chart, index) => (
            <Tab
              key={chart.id}
              sx={{ minHeight: 48 }}
              label={
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <span>{`Chart ${index + 1}`}</span>
                  {charts.length > 1 && (
                    // A span, not the icon itself: MUI marks its SVGs
                    // aria-hidden, and a nested <button> inside the Tab's own
                    // button is invalid.
                    <Box
                      component="span"
                      role="button"
                      aria-label={`Close chart ${index + 1}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        closeChart(index);
                      }}
                      sx={{ display: 'flex', opacity: 0.6, '&:hover': { opacity: 1 } }}
                    >
                      <CloseIcon fontSize="inherit" />
                    </Box>
                  )}
                </Stack>
              }
            />
          ))}
        </Tabs>
        <Tooltip title="Add chart">
          <IconButton color="primary" onClick={addChart} aria-label="Add chart" size="small">
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Box sx={{ flexGrow: 0 }} />
        {props.onRefresh && (
          <IconButton color="primary" onClick={props.onRefresh} disabled={props.refreshing} title="Refresh Data">
            <Iconify icon="eva:refresh-fill" />
          </IconButton>
        )}
        {props.openExport && (
          <IconButton color="primary" onClick={() => props.openExport()} title="Export Data">
            <Iconify icon="eva:download-fill" />
          </IconButton>
        )}
      </Stack>
      <Divider />
      <SessionChartTab
        key={charts[current]?.id}
        channels={channels}
        selectedKeys={charts[current]?.keys || []}
        onSelectedKeysChange={(keys) => setKeys(current, keys)}
        events={props.events}
        currentTime={props.currentTime}
        onChartClick={props.onChartClick}
        eventSensor={props.eventSensor}
        setSensor={props.setSensor}
      />
    </Card>
  );
}
